// Validation côté thème des produits offerts (_nooance_free_gift).
//
// L'extension checkout (nooance-checkout-extensions / free-products) ajoute au
// panier des lignes cadeaux marquées de la propriété `_nooance_free_gift = 'true'`
// quand les conditions d'une campagne sont remplies (paliers de montant, VIP,
// produit requis…). Mais le checkout ne RETIRE ces cadeaux que dans le mode
// sans cumul ('highest') : en mode 'all', un cadeau ajouté reste au panier même
// si le client repasse SOUS le seuil côté thème (suppression d'un article,
// baisse de quantité…). Résultat : un cadeau « orphelin » non mérité persiste.
//
// Ce module rejoue la MÊME logique de conditions (free-gift-engine.js, miroir de
// l'engine.js du checkout) sur l'état réel du panier et retire les lignes
// cadeaux qui ne sont plus éligibles. Il ne fait que RETIRER (l'ajout reste la
// responsabilité de l'extension checkout).
//
// Déclenché : au chargement (si le panier contient déjà un cadeau) et à chaque
// `cart:update` (le client modifie son panier).

import {
  parseShopConfig,
  parseCampaignLogic,
  parseGiftStacking,
  parseShopTimezone,
  activeConditions,
  evaluateConditions,
  applyGiftStacking,
  collectGiftVariantIds,
} from '@theme/free-gift-engine';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const GIFT_PROPERTY = '_nooance_free_gift';
const DATA_ELEMENT_ID = 'free-gift-data';
const SOURCE = 'free-gift-validator';

/**
 * Données injectées par Liquid (snippets/scripts.liquid) :
 * { config: <objet|chaîne de la config metafield shop>, isVip: boolean, hasGift: boolean }
 * @returns {{config: any, isVip: boolean, hasGift: boolean} | null}
 */
function readData() {
  const el = document.getElementById(DATA_ELEMENT_ID);
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/**
 * Reconstruit la forme `entries` attendue par l'engine (miroir de
 * useAppMetafields côté checkout) à partir de la valeur brute du metafield.
 * @param {any} rawConfig
 */
function configEntries(rawConfig) {
  return [{ metafield: { key: 'free_products_with_minimum_amount', value: rawConfig } }];
}

/** @param {number|string} id */
const toVariantGid = (id) => `gid://shopify/ProductVariant/${id}`;
/** @param {number|string} id */
const toProductGid = (id) => `gid://shopify/Product/${id}`;

/**
 * Une ligne de panier est-elle un cadeau ajouté par l'extension ?
 * @param {any} item - ligne de /cart.js
 */
function isGiftLine(item) {
  return item?.properties?.[GIFT_PROPERTY] === 'true';
}

/**
 * La devise active du panier est-elle exploitable par la config ? Vrai s'il
 * n'existe aucune condition de montant (devise indifférente) OU si au moins une
 * condition de montant définit un seuil pour cette devise. Faux si des
 * conditions de montant existent mais qu'AUCUNE ne couvre la devise : on ne
 * pourra pas évaluer les paliers (Shopify Markets — panier basculé d'EUR vers
 * une devise non configurée comme CAD/CHF), il ne faut alors rien retirer.
 * @param {any[]} conditions
 * @param {string} currencyCode
 */
function currencySupported(conditions, currencyCode) {
  let hasAmountCondition = false;
  for (const c of conditions) {
    for (const ct of c.conditionTypes || []) {
      if (ct.type === 'minimum_amount' && ct.value) {
        hasAmountCondition = true;
        if (Object.prototype.hasOwnProperty.call(ct.value, currencyCode)) return true;
      }
    }
  }
  return !hasAmountCondition;
}

/**
 * Ensemble des IDs (GID) de variantes ACTUELLEMENT éligibles comme cadeaux,
 * calculé exactement comme le checkout (Checkout.jsx) : évaluation des
 * conditions, ET/OU entre campagnes, puis cumul ('all') ou plus-haut-palier
 * ('highest').
 *
 * - Sous-total calculé HORS lignes cadeaux : un cadeau (offert, à 0 €) ne doit
 *   pas compter pour débloquer son propre palier ni, en mode 'highest', faire
 *   franchir un palier supérieur (cascade).
 * - Les IDs variantes/produits du panier incluent en revanche TOUTES les lignes
 *   (cadeaux compris), pour reproduire le checkout à l'identique (Checkout.jsx
 *   construit cartVariantIds/cartProductIds sur toutes les lignes — pertinent
 *   pour les conditions `product_in_cart`).
 *
 * Renvoie null quand la devise active n'est couverte par aucune condition de
 * montant : « on ne peut pas décider » → l'appelant ne retire rien.
 *
 * @param {any} rawConfig
 * @param {{items: any[], currency: string}} cart
 * @param {boolean} isVip
 * @returns {Set<string> | null}
 */
function eligibleGiftVariantIds(rawConfig, cart, isVip) {
  const entries = configEntries(rawConfig);
  const conditions = parseShopConfig(entries);
  if (!conditions.length) return new Set();

  const currencyCode = cart.currency || 'USD';
  if (!currencySupported(conditions, currencyCode)) return null;

  const campaignLogic = parseCampaignLogic(entries);
  const giftStacking = parseGiftStacking(entries);
  const shopTimezone = parseShopTimezone(entries);

  const subtotalAmount =
    cart.items
      .filter((item) => !isGiftLine(item))
      .reduce((sum, item) => sum + (item.final_line_price || 0), 0) / 100;
  const cartVariantIds = new Set(cart.items.map((item) => toVariantGid(item.variant_id)));
  const cartProductIds = new Set(cart.items.map((item) => toProductGid(item.product_id)));

  const ctx = {
    subtotalAmount,
    currencyCode,
    isVip,
    cartVariantIds,
    cartProductIds,
    now: new Date(),
    shopTimezone,
  };

  const met = evaluateConditions(conditions, ctx);
  // ET entre campagnes : tout ou rien (les campagnes expirées ne comptent pas).
  const all =
    campaignLogic !== 'and'
      ? met
      : met.length && met.length === activeConditions(conditions, ctx).length
        ? met
        : [];
  const { eligible } = applyGiftStacking(all, giftStacking, currencyCode);
  return new Set(collectGiftVariantIds(eligible));
}

let running = false;

/**
 * Vérifie le panier et retire les cadeaux qui ne sont plus mérités.
 * @param {boolean} [fromLoad] - appel au chargement de page
 */
async function validateCart(fromLoad = false) {
  if (running) return;

  const data = readData();
  // Fail-safe : pas de config rendue (metafield absent) → on ne touche à rien.
  const rawConfig = data?.config;
  const hasConfig =
    rawConfig != null && (typeof rawConfig === 'object' || String(rawConfig).trim() !== '');
  if (!hasConfig) return;

  // Au chargement, on évite un appel /cart.js inutile si Liquid a déjà constaté
  // l'absence de cadeau dans le panier rendu.
  if (fromLoad && data.hasGift === false) return;

  running = true;
  try {
    const cartUrl = `${Theme.routes.cart_url}.js`;
    const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

    const giftLines = cart.items.filter(isGiftLine);
    if (giftLines.length === 0) return;

    const eligibleIds = eligibleGiftVariantIds(rawConfig, cart, data.isVip === true);
    // null = devise active non couverte par la config → on ne peut pas décider,
    // on ne retire rien (évite de supprimer un cadeau mérité après bascule de devise).
    if (eligibleIds == null) return;
    const orphans = giftLines.filter((line) => !eligibleIds.has(toVariantGid(line.variant_id)));
    if (orphans.length === 0) return;

    await removeLines(orphans);
  } catch (error) {
    console.error('[free-gift-validator]', error);
  } finally {
    running = false;
  }
}

/**
 * Retire les lignes données (quantité 0) et rafraîchit l'UI panier (sections
 * cart-items présentes + bulle d'icône) via un CartUpdateEvent.
 * @param {any[]} lines
 */
async function removeLines(lines) {
  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of lines) updates[line.key] = 0;

  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
        .filter(Boolean)
    ),
  ];

  const body = JSON.stringify({
    updates,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });

  const cart = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body })).then(
    (r) => r.json()
  );

  // Réponse d'erreur Shopify (ex. clé de ligne périmée → {status, message…} sans
  // item_count) : ne pas diffuser un itemCount erroné (mettrait la bulle à 0).
  // L'UI se resynchronisera au prochain changement de panier / rechargement.
  if (cart?.errors || typeof cart?.item_count !== 'number') return;

  document.dispatchEvent(
    new CartUpdateEvent({}, SOURCE, {
      itemCount: cart.item_count,
      source: SOURCE,
      sections: cart.sections,
    })
  );
}

function init() {
  // 1) Au chargement : retire un cadeau orphelin laissé par une session
  //    précédente (panier persistant, abandon de checkout…).
  validateCart(true);

  // 2) À chaque changement de panier (suppression d'article, baisse de quantité…)
  //    on re-vérifie — en ignorant nos propres mises à jour pour éviter une boucle.
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (/** @type {any} */ (event)?.detail?.data?.source === SOURCE) return;
    validateCart(false);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
